// cxm-admin-web/src/container/label-template-management/components/preview.js

import React, { useState, useRef, useEffect } from 'react';

/**
 * 标签打印买家预览组件
 * @param {Object} props
 * @param {number} props.pageWidth - 页面宽度（单位：mm）
 * @param {number} props.pageHeight - 页面高度（单位：mm）
 * @param {Array} props.fields - 字段配置数组
 * @param {Object} props.data - 预览数据（可选，预览时可传入模拟数据）
 * @param {boolean} props.draggable - 是否支持拖拽（编辑模式）
 * @param {Function} props.onFieldChange - 字段变更回调（编辑模式）
 */
export const printScale = 8; // 缩放倍数，便于在屏幕上显示

const defaultData = {
  buyerName: '标签打印买家',
  mainBuyerName: '主买家',
  buyerRemark: '这是买家备注',
  dateTime: 'xxxx-xx-xx',
  productName: '标签打印货品',
  saleNumber: 'xx件',
  saleWeight: 'xxx公斤',
  goodsRemark: '这是一条单品备注，最多显示两行，超过两行会被截断...',
  adContent: '诚信档口，就用菜小秘',
};

const Preview = ({
  pageWidth,
  pageHeight,
  fields,
  yLine,
  showMasterBuyerName = true,
  draggable = false,
  onFieldChange,
}) => {
  console.log('fields: ', fields);

  // 拖拽状态（仅在编辑模式下使用）
  const [draggedField, setDraggedField] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const previewRef = useRef(null);

  // 合并默认数据和传入数据
  const previewData = { ...defaultData };

  // 字号映射（可根据实际需求调整）
  const fontSizeMap = {
    1: 24,
    2: 32,
    3: 46,
    4: 62,
    6: 88,
  };

  // 拖拽相关函数（仅在编辑模式下使用）
  const handleMouseDown = (e, field) => {
    if (!draggable || !field.show) return;

    const rect = previewRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - field.x;
    const offsetY = e.clientY - rect.top - field.y;

    setDraggedField(field);
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    setSelectedField(field);

    e.preventDefault();
  };

  // 点击选中字段（仅在编辑模式下使用）
  const handleFieldClick = (e, field) => {
    if (!draggable || !field.show) return;
    setSelectedField(field);
    e.stopPropagation();
  };

  const handleMouseMove = (e) => {
    if (!draggable || !isDragging || !draggedField) return;
    const rect = previewRef.current.getBoundingClientRect();
    const newY = Math.max(
      0,
      Math.min(e.clientY - rect.top - dragOffset.y, pageHeight * printScale - 20),
    );

    // 调用父组件的回调函数更新字段位置
    if (onFieldChange) {
      onFieldChange(draggedField.key, 'y', newY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedField(null);
  };

  // 添加全局事件监听（仅在编辑模式下）
  useEffect(() => {
    if (draggable && isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggable, isDragging, draggedField, dragOffset, pageWidth, pageHeight]);

  // 渲染字段
  const renderField = (field) => {
    const { key, x, y, size, isBold, maxLine, title, fixed, align, showTitle, show } = field;

    // 在编辑模式下，只渲染显示的字段
    if (draggable && !show) return null;

    // 取内容
    let content = '';
    if (fixed) {
      content = previewData[key] || '';
    } else {
      const mainBuyerName =
        key === 'buyerName' && showMasterBuyerName && previewData.mainBuyerName
          ? `(${previewData.mainBuyerName})`
          : '';
      content = (title && showTitle ? `${title}` : '') + (previewData[key] || '') + mainBuyerName;
    }

    // 字体样式
    const style = {
      position: 'absolute',
      left: x, // 适当缩放，便于预览
      top: y,
      maxWidth: '100%',
      fontWeight: isBold ? 'bold' : 'normal',
      fontSize: fontSizeMap[size] || 14,
      lineHeight: 1,
      whiteSpace: 'pre-line',
      overflow: 'hidden',
      textAlign: align ? align : 'left',
      width: align === 'center' ? pageWidth * printScale - 40 : 'auto',
      color: 'rgba(0, 0, 0, 0.88)',
      // 编辑模式下的额外样式
      ...(draggable && {
        cursor: 'move',
        userSelect: 'none',
        border: selectedField?.key === key ? '2px solid #1890ff' : '1px solid transparent',
        backgroundColor: selectedField?.key === key ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
        padding: '2px 4px',
        borderRadius: '4px',
        transition: 'all 0.2s',
      }),
    };

    // 限制最大行数
    const lineClampStyle = maxLine
      ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLine,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : {};

    if (key === 'line') {
      return (
        <div
          key={key}
          style={{
            position: 'absolute',
            left: x,
            top: y,
            right: x,
            height: size * 2,
            background: '#ccc',
            // 编辑模式下的额外样式
            ...(draggable && {
              cursor: 'move',
              border: selectedField?.key === key ? '2px solid #1890ff' : '1px solid transparent',
              transition: 'all 0.2s',
            }),
          }}
          onMouseDown={draggable ? (e) => handleMouseDown(e, field) : undefined}
          onClick={draggable ? (e) => handleFieldClick(e, field) : undefined}
        />
      );
    }
    return (
      <div
        key={key}
        style={{ ...style, ...lineClampStyle }}
        onMouseDown={draggable ? (e) => handleMouseDown(e, field) : undefined}
        onClick={draggable ? (e) => handleFieldClick(e, field) : undefined}
      >
        {content}
      </div>
    );
  };

  return (
    <div
      ref={previewRef}
      style={{
        position: 'relative',
        width: pageWidth * printScale,
        height: pageHeight * printScale,
        border: '2px solid #ccc',
        borderRadius: 12,
        background: '#fff',
        margin: '0 auto',
        boxSizing: 'content-box',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      onClick={() => setSelectedField(null)} // 点击空白区域取消选中
    >
      {/* 渲染所有字段 */}
      {fields.map(renderField)}
      {/* 以10为刻度渲染分割线 */}
      {Array.from({ length: Math.floor((pageHeight * printScale) / 10) + 1 }, (_, i) => (
        <React.Fragment key={`line-${i}`}>
          {/* 刻度线 */}
          <div
            style={{
              position: 'absolute',
              left: -15,
              top: i * 10,
              width: 15,
              height: 1,
              background: '#5e5e5e',
              zIndex: 1,
            }}
          />
          {/* 刻度数值 */}
          <div
            style={{
              position: 'absolute',
              left: -35,
              top: i * 10 - 7,
              width: 20,
              fontSize: 10,
              color: '#888',
              textAlign: 'right',
              zIndex: 2,
              userSelect: 'none',
            }}
          >
            {i * 10}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default Preview;
